<template>
  <div id="app" class="min-h-screen font-hanken">
    <!-- Header -->
    <header 
      ref="header"
      class="header-site"
      :class="headerClasses"
      :style="{ 
        height: headerHeight + 'px',
        cursor: isHeaderClickable ? 'pointer' : 'default'
      }"
      @click="handleHeaderClick"
    >
      <div class="w-full h-full relative">
        <div class="flex items-center h-full relative w-full px-2 lg:px-4">
          <!-- Logo -->
          <div class="flex items-center z-10">
            <router-link :to="getLocalizedRoute('')" @click.stop="closeMenu">
              <img 
                ref="logo"
                src="/assets/images/logo-faz.svg" 
                alt="FAZ" 
                class="transition-all duration-300"
                :class="logoClasses"
                :style="{ 
                  width: logoWidth + 'px', 
                  height: 'auto',
                  filter: (!isOverVideo || !showEffectDifference) ? 'brightness(0) saturate(100%)' : 'none'
                }"
              >
            </router-link>
          </div>
          
          <!-- Texto Central - Desktop (com efeito de movimento limitado) -->
          <div 
            ref="headerText"
            class="absolute transition-all duration-300 hidden lg:block"
            :style="{ 
              left: textPosition + '%', 
              transform: 'translateX(-50%)',
              fontSize: textSize + 'px',
              maxWidth: 'calc(100vw - 200px)'
            }"
            @click.stop
          >
            <div class="text-center">
              <span 
                class="font-hanken tracking-wider font-medium block whitespace-nowrap"
                :class="textClasses"
              >
                {{ t('common.acontecer') }}
              </span>
              <span 
                v-if="!isHomePage"
                class="font-hanken tracking-wider font-semibold block mt-1 text-lg"
                :class="textClasses"
              >
                {{ t('common.' + (pageTitleKey || 'falamos')) }}
              </span>
            </div>
          </div>

          <!-- Texto Central - Mobile/Tablet (posicionado entre logo e controles) -->
          <div 
            class="absolute left-0 right-0 lg:hidden flex items-center justify-center"
            @click.stop
          >
            <div class="text-center">
              <span 
                class="font-hanken tracking-wider font-medium block whitespace-nowrap text-sm"
                :class="textClasses"
              >
                {{ t('common.acontecer') }}
              </span>
              <span 
                v-if="!isHomePage"
                class="font-hanken tracking-wider font-semibold block mt-1 text-base"
                :class="textClasses"
              >
                {{ t('common.' + (pageTitleKey || 'falamos')) }}
              </span>
            </div>
          </div>
          
          <div class="flex items-center justify-center gap-0">
            <!-- Language Selector -->
            <div 
              class="absolute right-[55px] flex items-center justify-center transition-all duration-500 ease-out whitespace-nowrap"
              :class="{ 'opacity-0 scale-90': progress < 1 && !isMenuOpen, 'opacity-100 scale-100': progress >= 1 || isMenuOpen }"
              @click.stop
            >
              <LanguageSelector 
                :buttonClasses="hamburgerColorString"
              />
            </div>

            <!-- Hamburger Menu -->
            <div 
                ref="hamburgerContainer"
                class="absolute right-0 flex items-center justify-center w-16 h-20 transition-all duration-500 ease-out"
                :class="{ 'opacity-0 scale-90': progress < 1 && !isMenuOpen, 'opacity-100 scale-100': progress >= 1 || isMenuOpen }"
              >
              <button
                @click.stop="toggleMenu"
                class="flex flex-col justify-center items-center space-y-2 transition-colors duration-300"
                :class="hamburgerColorClass"
              >
                <span class="hamburger-line w-8 h-0.5 bg-current"></span>
                <span class="hamburger-line w-8 h-0.5 bg-current"></span>
                <span class="hamburger-line w-8 h-0.5 bg-current"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Mobile Menu Component -->
    <MobileMenu :isMenuOpen="isMenuOpen" :logoWidth="logoWidth" @close="closeMenu" />

    <!-- Loading Screen -->
    <LoadingScreen 
      :isVisible="isLoading" 
      :progress="loadedVideos" 
      :totalVideos="totalVideos" 
    />

    <!-- Main Content -->
    <main class="flex-1">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" @video-loaded="markVideoLoaded" />
        </transition>
      </router-view>
    </main>
   
    <!-- Footer -->
    <footer class="bg-black text-white py-12 sm:py-16 px-6">
      <div class="max-w-6xl mx-auto">
        <!-- Título Principal -->
        <div class="text-center mb-8 sm:mb-12">
          <h2 class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-tight" v-html="t('footer.title')">
          </h2>
        </div>
        
        <!-- Links de Navegação -->
        <div class="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 mb-12 sm:mb-16">
          <router-link :to="getLocalizedRoute('about')" class="footer-link text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
            {{ t('common.quemSomos') }}
          </router-link>
          <router-link :to="getLocalizedRoute('contact')" class="footer-link text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
            {{ t('common.falamos') }}
          </router-link>
          <!-- Work With Us oculto
          <router-link :to="getLocalizedRoute('work-with-us')" class="footer-link text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
            {{ t('common.vagas') }}
          </router-link>
          -->
          <a href="mailto:hello@wearefaz.com" class="footer-link text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
            HELLO@WEAREFAZ.COM
          </a>
          <a href="https://www.instagram.com/faz.acontecer__?igsh=MTM2ZWlqOXB2OTA2YQ==" target="_blank" class="footer-link text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
            {{ t('common.instagram') }}
          </a>
          <a v-if="false" href="#" class="footer-link text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
            {{ t('common.linkedin') }}
          </a>
        </div>
        
        <!-- Copyright -->
        <div class="text-center md:text-left">
          <p class="text-xs md:text-sm text-white font-light">
            {{ t('common.copyright') }}
          </p>
        </div>
      </div>
    </footer>
    
  </div>
</template>

<script>
import MobileMenu from './components/MobileMenu.vue'
import LoadingScreen from './components/LoadingScreen.vue'
import LanguageSelector from './components/LanguageSelector.vue'
import { useVideoLoading } from './composables/useVideoLoading'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

export default {
  name: 'App',
  components: {
    MobileMenu,
    LoadingScreen,
    LanguageSelector
  },
  setup() {
    const {
      isLoading,
      loadedVideos,
      totalVideos,
      markVideoLoaded,
      forceFinishLoading
    } = useVideoLoading()

    const { t, locale } = useI18n()
    const route = useRoute()

    // Função para gerar rotas localizadas
    const getLocalizedRoute = (routeName) => {
      const currentLocale = locale.value === 'en-US' ? 'en' : 'pt'
      return routeName ? `/${currentLocale}/${routeName}` : `/${currentLocale}`
    }

    return {
      isLoading,
      loadedVideos,
      totalVideos,
      markVideoLoaded,
      forceFinishLoading,
      getLocalizedRoute,
      t
    }
  },
  data() {
    return {
      // Estado do menu mobile
      isMenuOpen: false,
      
      // Estado do header (será sobrescrito pelas configurações da página)
      isOverVideo: true,
      progress: 0,
      
      // Dimensões do header (serão configuradas dinamicamente)
      headerHeight: 400,
      logoWidth: 380,
      textSize: 24,
      textPosition: 92,
      
      // Flags de controle
      isInitialLoad: true,
      showEffectDifference: true
    }
  },
  computed: {
    // Detectar se é página home (único caso especial)
    isHomePage() {
      const routeName = this.$route.name
      return routeName === 'Home-en' || routeName === 'Home-pt' || this.$route.path === '/en' || this.$route.path === '/pt'
    },

    // Detectar se o header é clicável (para cursor pointer)
    isHeaderClickable() {
      if (!this.isHomePage) return false
      if (typeof window === 'undefined') return false
      return this.headerHeight > window.innerHeight * 0.9
    },


    // Classes do header - apenas 2 modos
    headerClasses() {
      return {
        'bg-white header-shadow': !this.isHomePage,
        'bg-transparent': this.isHomePage,
        'effect-difference': this.isHomePage && this.showEffectDifference
      }
    },

    logoClasses() {
      return {
        'logo-blend-negative': this.isHomePage && this.showEffectDifference
      }
    },

    textClasses() {
      return {
        'text-blend-negative': this.isHomePage && this.showEffectDifference,
        'text-gray-900': !this.isHomePage || !this.showEffectDifference
      }
    },

    hamburgerColorClass() {
      return {
        'text-white': (this.isHomePage && this.showEffectDifference) || this.isMenuOpen,
        'text-gray-900': (!this.isHomePage || !this.showEffectDifference) && !this.isMenuOpen
      }
    },

    hamburgerColorString() {
      return (this.isHomePage && this.showEffectDifference) || this.isMenuOpen ? 'text-white' : 'text-gray-900'
    },

    pageTitleKey() {
      const routeName = this.$route.name
      if (routeName === 'About-en' || routeName === 'About-pt') return 'quemSomos'
      if (routeName === 'Contact-en' || routeName === 'Contact-pt') return 'falamos'
      if (routeName === 'WorkWithUs-en' || routeName === 'WorkWithUs-pt') return 'vagas'
      if (routeName === 'AccountManager-en' || routeName === 'AccountManager-pt') return 'vagas'
      if (routeName === 'ArtDirector-en' || routeName === 'ArtDirector-pt') return 'vagas'
      return ''
    }
  },
  mounted() {
    this.setupInitialValues()
    this.setupVideoLoadingListeners()
    this.setupScrollListener()
    
    // Bloquear scroll inicialmente
    document.body.style.overflow = 'hidden'
    document.body.classList.add('loading-active')
    window.scrollTo(0, 0)
    
    // Listener para redimensionamento da janela
    window.addEventListener('resize', this.handleResize)
    
    // Fallback de segurança para o loading
    this.forceFinishLoading()
  },
      watch: {
      '$route'() {
        this.setupInitialValues()
        // Fechar menu se estiver aberto ao mudar de rota
        if (this.isMenuOpen) {
          this.closeMenu()
        }
        // Ir para o topo sem smooth scroll
        window.scrollTo(0, 0)
      },
      
      isLoading(newValue) {
        // Controla o scroll do body quando loading está ativo
        if (newValue) {
          document.body.style.overflow = 'hidden'
          document.body.classList.add('loading-active')
          // Forçar scroll para o topo
          window.scrollTo(0, 0)
        } else {
          document.body.style.overflow = 'auto'
          document.body.classList.remove('loading-active')
        }
      },
      

    },
  beforeUnmount() {
    // Restaurar overflow do body
    document.body.style.overflow = 'auto'
    
    // Remover listeners
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    
    closeMenu() {
      this.isMenuOpen = false
    },
    
    setupInitialValues() {
      if (this.isHomePage) {
        // Modo HOME: Header transparente com efeitos (estado inicial)
        // As dimensões serão controladas pelo GSAP no VideoHero
        this.headerHeight = window.innerHeight * 1 // Altura inicial maior para o GSAP
        this.logoWidth = 380
        this.textSize = 24
        this.textPosition = this.getTextPositionForHome()
        this.isOverVideo = true
        this.progress = 0
        this.showEffectDifference = true
      } else {
        // Modo NÃO-HOME: Header fixo branco
        this.headerHeight = 80
        this.logoWidth = window.innerWidth <= 768 ? 93 : 104
        this.textSize = 12
        this.textPosition = 50
        this.isOverVideo = false
        this.progress = 1
        this.showEffectDifference = false
      }
    },

    /**
     * Calcular posição do texto para home baseada no breakpoint
     */
    getTextPositionForHome() {
      const width = window.innerWidth
      if (width <= 768) return 50      // Mobile: centralizado
      if (width <= 1024) return 87     // Tablet: direita
      if (width <= 1280) return 92     // Desktop: direita
      return 92                        // Desktop grande: direita
    },
    
    setupVideoLoadingListeners() {
      // Listener para eventos de vídeo carregado
      window.addEventListener('video-loaded', (event) => {
        this.markVideoLoaded(event.detail)
      })
    },

    setupScrollListener() {
      // Listener de scroll para animar o header na home
      window.addEventListener('scroll', this.handleScroll)
    },

    handleScroll() {
      // Só animar na home page
      if (!this.isHomePage) return

      // Usar exatamente o mesmo cálculo de progresso que o GSAP usa
      const scrollY = window.scrollY
      const windowHeight = window.innerHeight
      const maxScroll = windowHeight * 0.6 // Mesmo valor usado no GSAP
      const progress = Math.min(scrollY / maxScroll, 1)
      
      // Aplicar a mesma função de easing que o GSAP usa
      const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4)
      const easedProgress = easeOutQuart(progress)
      
      // Atualizar progresso
      this.progress = progress
      
      // Mostrar fundo branco quando a animação GSAP estiver quase completa
      // Usar progresso linear para melhor sincronização
      if (progress > 0.98) {
        this.isOverVideo = false
        this.showEffectDifference = false
      } else {
        this.isOverVideo = true
        this.showEffectDifference = true
      }
    },
    
    handleResize() {
      // Atualizar posição do texto apenas na home
      if (this.isHomePage) {
        this.textPosition = this.getTextPositionForHome()
      }
    },
    
    handleHeaderClick(event) {
      // Só executar na home page
      if (!this.isHomePage) {
        return
      }
      
      // Verificar se o header está expandido (altura próxima a 100vh)
      const isHeaderExpanded = this.headerHeight > window.innerHeight * 0.9
      
      // Só fazer toggle do som se o header estiver expandido
      if (isHeaderExpanded) {
        // Disparar evento customizado para o VideoHero fazer o toggle
        window.dispatchEvent(new CustomEvent('toggle-video-sound'))
      }
    }
  }
}
</script>

<style scoped>
/* Hamburger Lines */
.hamburger-line {
  transform-origin: center;
}

/* Transições suaves */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Otimizações para performance */
.fixed {
  will-change: transform;
}

img {
  will-change: transform, opacity;
}

/* Garantir que o header não interfira com o conteúdo */
header {
  will-change: height;
}

/* Shadow suave para o header fixo */
.header-shadow {
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
}

/* Efeito Blend Mode Negativo */
.logo-blend-negative {
  mix-blend-mode: difference;
  filter: brightness(0) invert(1);
}

.text-blend-negative {
  color: white;
  mix-blend-mode: difference;
}

/* Footer Links com Underline Animado */
.footer-link {
  position: relative;
  text-decoration: none;
  transition: color 0.3s ease;
  display: inline-block;
}

.footer-link::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background-color: currentColor;
  transition: width 0.3s ease;
}

.footer-link:hover::before {
  width: 100%;
}

/* Responsividade adicional para o footer */
@media (max-width: 640px) {
  .footer-link {
    padding: 4px 0;
  }
}

/* Transições de Página */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

.page-enter-to,
.page-leave-from {
  opacity: 1;
}

/* Remover smooth scroll durante loading */
body.loading-active {
  scroll-behavior: auto !important;
}

/* Garantir que o loading fique sempre no topo */
.loading-screen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 9999 !important;
}
</style> 